import { useEffect, useMemo, useState } from "react";
import { type OrderWithLines } from "@kanpla/api-contract";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faWarning,
  faCheck,
  faInfoCircle
} from "@fortawesome/pro-solid-svg-icons";
import { useSearchParams } from "react-router-dom";

import { refundActions, useRefundInfo } from "~/providers/store/refund";
import { T, useT } from "@repo/transifex";
import {
  formatRefundOrderLines,
  type FormattedOrderLine
} from "~/utils/refund-line-formatter";
import { useAppContext, useAppDispatch } from "~/providers/app";
import {
  useTransactionStatus,
  useAbortTransaction
} from "~/hooks/queries/terminals";
import { useOrderRefund, useOrder } from "~/hooks/queries/orders";
import { priceFormatter } from "~/utils/price-formatter";
import { isApiError } from "~/api/error";
import { TerminalReadyIcon } from "~/routes/__app.pos.sell.card/TerminalReadyIcon";
import { type Currency } from "~/api/products/types";
import { useCurrentTill } from "~/api/tills/hooks/use-till-current";
import { useCurrentTillLocalisation } from "~/api/tills/hooks/use-till-current/localisation";
import { type PaymentProvider } from "~/api/tills/types";
import { logger } from "~/services/logger";
import { useRefundPoiPayment } from "~/hooks/freedompay/use-refund-poi-payment";
import { autoId } from "~/utils/auto-id";
import { minorUnitToMajorUnit } from "@repo/system";

import { CheckboxButton } from "../CheckboxButton";
import { Button } from "../Button";
import { RadioButton } from "../RadioButton";
import { LoadingSpinner } from "../LoadingSpinner";
import { toast } from "../hooks/use-toast";
import { DisplayErrorMessage } from "../display-error-message";

type RefundModalProps = {
  orderId: string;
  onClose: () => void;
};

export const RefundModal = ({ orderId, onClose }: RefundModalProps) => {
  const dispatch = useAppDispatch();

  const [searchParams] = useSearchParams();
  const serviceId = searchParams.get("serviceId");

  const { locale } = useCurrentTillLocalisation();
  const { data: till } = useCurrentTill();
  const terminalId =
    (till?.paymentGatewayConfig?.provider === "freedompay"
      ? till.freedompayTerminalId
      : till?.terminalId) ?? undefined;
  const provider = till?.paymentGatewayConfig?.provider;

  const {
    data: order,
    status: orderStatus,
    error: orderError,
    refetch: refetchOrder,
    isFetching: isOrderFetching
  } = useOrder(orderId);

  const { mutate: refundOrder, status: refundStatus } = useOrderRefund();
  const { mutate: refundPoiPayment, status: refundPoiPaymentStatus } =
    useRefundPoiPayment();

  const t = useT();

  const { mutate: abortTransaction, status: abortTransactionStatus } =
    useAbortTransaction();
  useTransactionStatus({ serviceId, orderId, terminalId });

  const refundInfo = useRefundInfo();

  const [openChooseMethodDialog, setOpenChooseMethodDialog] = useState(false);
  const [isCreditRefund, setIsCreditRefund] = useState(false);

  const handleClose = () => {
    dispatch({ type: "PRINTER_PRINT_RECEIPT_RESET" });
    onClose();
  };

  if (orderStatus === "pending" || isOrderFetching) {
    return <LoadingSpinner />;
  }

  if (orderError) {
    const errorMessage = isApiError(orderError)
      ? orderError.body.message
      : t("Could not fetch order");

    return <ErrorMessage error={errorMessage} />;
  }

  const isSplitPayment = order.payments && order.payments.length > 1;

  const transactionReference = isSplitPayment
    ? undefined
    : order.payments?.find(
        (p: (typeof order.payments)[number]) =>
          p.paymentProvider === "card" || p.paymentProvider === "swish"
      )?.transactionReference;

  const isUnreferencedRefund = !transactionReference;

  const isBilling = order.payments?.some(
    (p) => p.paymentProvider === "billing"
  );

  // The order was paid in full with Swish
  const isFullSwish = Boolean(
    order.payments?.some((payment) => payment.paymentProvider === "swish") &&
      order.payments?.length === 1
  );

  const isHybridBilled = order.discounts?.some(
    (d) =>
      d.discountType === "hybridBilling" ||
      d.discountType === "discount-hybridBilling"
  );

  const unitTotalRefundAmountInclVat = refundInfo.selectedOrderLines.reduce(
    (acc, { amount, unitRefundAmountInclVat }) =>
      acc + amount * unitRefundAmountInclVat,
    0
  );

  const unitTotalRefundTaxAmount = refundInfo.selectedOrderLines.reduce(
    (acc, { amount, unitRefundTaxAmount }) =>
      acc + amount * unitRefundTaxAmount,
    0
  );

  const formattedRefundLines = formatRefundOrderLines(
    order,
    order.currency,
    locale
  );

  if (refundStatus === "pending" || refundPoiPaymentStatus === "pending") {
    if (
      refundInfo.refundMethod === "card" &&
      !isCreditRefund &&
      isUnreferencedRefund &&
      !isBilling
    ) {
      return (
        <div className="flex flex-col items-center gap-y-5 p-5">
          <p className="text-base">
            <T _str="Amount" />
          </p>

          <p className="text-2xl font-bold">
            {priceFormatter({
              value: unitTotalRefundAmountInclVat,
              currency: order.currency,
              locale
            })}
          </p>

          <div className="flex w-full flex-col items-center gap-5 rounded-2xl bg-background-secondary p-5">
            <p className="text-lg font-bold text-primary-light">
              <T _str="Terminal ready" />
            </p>

            <TerminalReadyIcon />
          </div>

          <Button
            disabled={!orderId || !terminalId}
            loading={abortTransactionStatus === "pending"}
            onClick={() => {
              if (!terminalId) {
                throw new Error("Cannot abort transaction: missing terminalId");
              }
              abortTransaction({ orderId, serviceId });
              handleClose();
            }}
            variant="danger"
          >
            <T _str="Cancel" />
          </Button>
        </div>
      );
    }
    return <LoadingSpinner />;
  }

  if (order?.totalPriceUnit === 0 && !isHybridBilled) {
    return (
      <ConfirmDialog
        onConfirm={() => {
          refundOrder(
            {
              orderId,
              refundMethod: "cash",
              shouldGenerateServiceId: isUnreferencedRefund,
              refundOrderLines: refundInfo.selectedOrderLines
            },
            { onSettled: handleClose }
          );
        }}
        onOutsideClick={handleClose}
        title={t("Confirm refund")}
      />
    );
  }

  if (openChooseMethodDialog) {
    if (order.payments?.some((p) => p.paymentProvider === "billing")) {
      return (
        <ConfirmDialog
          onConfirm={() => {
            refundOrder(
              {
                orderId,
                refundMethod: "billing",
                shouldGenerateServiceId: isUnreferencedRefund,
                refundOrderLines: refundInfo.selectedOrderLines
              },
              { onSettled: handleClose }
            );
          }}
          onOutsideClick={handleClose}
          title={t("Confirm refund with invoice")}
        />
      );
    }

    if (
      (order?.paidWithSaldoUnit && order?.paidWithSaldoUnit > 0) ||
      (order.totalPriceUnit === 0 && isHybridBilled)
    ) {
      return (
        <ConfirmDialog
          title={t("Confirm refund hybrid billing")}
          onConfirm={() => {
            setIsCreditRefund(true);
            refundOrder(
              {
                orderId,
                refundMethod: "credit",
                shouldGenerateServiceId: isUnreferencedRefund,
                refundOrderLines: refundInfo.selectedOrderLines
              },
              { onSettled: handleClose }
            );
          }}
          onOutsideClick={handleClose}
        >
          <T _str="* If allowance was used, the user's allowance will be restored" />
        </ConfirmDialog>
      );
    }

    return (
      <ChooseRefundMethodDialog
        isFullSwish={isFullSwish}
        onConfirm={() => {
          if (refundInfo.refundMethod === "card" && provider === "freedompay") {
            if (!order.payments || order.payments.length < 1) {
              throw new Error("No payment found");
            }

            const refundPaymentId = autoId();
            logger.debug("FreedomPay: refund MRC minted", {
              order_id: orderId,
              payment_id: refundPaymentId,
              transaction_reference: transactionReference,
              event: "freedompay_refund_mrc_minted"
            });

            refundPoiPayment(
              {
                orderId,
                paymentId: refundPaymentId,
                amountInclVat: minorUnitToMajorUnit(
                  unitTotalRefundAmountInclVat
                ),
                taxTotal: minorUnitToMajorUnit(unitTotalRefundTaxAmount),
                transactionReference
              },
              {
                onSuccess: () => {
                  refundOrder(
                    {
                      orderId,
                      refundMethod: refundInfo.refundMethod,
                      shouldGenerateServiceId: isUnreferencedRefund,
                      refundOrderLines: refundInfo.selectedOrderLines
                    },
                    {
                      onSuccess: handleClose,
                      onError: (error) => {
                        logger.error(
                          error,
                          "Error refunding order after POI payment succeeded",
                          {
                            order_id: orderId
                          },
                          {
                            order_id: orderId
                          }
                        );
                      }
                    }
                  );
                },
                onError: (error) => {
                  toast({
                    title: <T _str="Error refunding POI payment" />,
                    variant: "destructive",
                    description: (
                      <DisplayErrorMessage
                        error={error}
                        fallback={<T _str="Could not refund POI payment" />}
                      />
                    )
                  });
                }
              }
            );
          } else {
            refundOrder(
              {
                orderId,
                refundMethod: refundInfo.refundMethod,
                shouldGenerateServiceId: isUnreferencedRefund,
                refundOrderLines: refundInfo.selectedOrderLines
              },
              {
                onSettled: handleClose
              }
            );
          }
        }}
        order={order}
        onOutsideClick={handleClose}
        currency={order.currency}
        provider={provider}
      />
    );
  }

  if (formattedRefundLines.length === 0) {
    return (
      <ConfirmDialog
        onConfirm={handleClose}
        onOutsideClick={handleClose}
        title={t("No items to refund")}
      >
        <Button
          className="my-8"
          onClick={() => refetchOrder()}
          variant="danger-light"
        >
          <T _str="Retry" />
        </Button>
      </ConfirmDialog>
    );
  }

  return (
    <DialogChooseOrderLines
      order={order}
      formattedOrderLines={formattedRefundLines}
      onConfirm={(orderLines) => {
        if (orderLines) {
          refundActions.setOrderLines({
            order,
            selectedOrderLines: orderLines.map((o) => o.id),
            formattedOrderLines: orderLines
          });
        }
        setOpenChooseMethodDialog(true);
      }}
      onClose={handleClose}
    />
  );
};

type DialogChooseOrderLinesProps = {
  order: OrderWithLines;
  formattedOrderLines: FormattedOrderLine[];
  onConfirm: (orderLines?: FormattedOrderLine[]) => void;
  isHybridBilled?: boolean;
  onClose: () => void;
};

const DialogChooseOrderLines = ({
  order,
  formattedOrderLines,
  onConfirm,
  isHybridBilled,
  onClose
}: DialogChooseOrderLinesProps) => {
  const t = useT();

  const [selectedOrderLines, setSelectedOrderLines] = useState<string[]>([]);

  const paymentMethods = (() => {
    if (order.payments) {
      return order.payments
        .map((payment) => {
          switch (payment.paymentProvider) {
            case "mobilepay_own":
              return "MobilePay";
            case "swish":
              return "Swish";
            case "card":
              return t("Card");
            case "billing":
              return t("Billing");
            case "cash":
              return t("Cash");
            case "credit":
              return t("Credit");
            default:
              return "";
          }
        })
        .join(", ");
    }
    return "";
  })();

  useEffect(() => {
    // If the order contains some hybrid billing, skip order lines selection
    if (formattedOrderLines.length === 1 || isHybridBilled) {
      onConfirm(formattedOrderLines);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- to avoid infinite re-renders
  }, []);

  const isBilling =
    order.payments?.some((payment) => payment.paymentProvider === "billing") ||
    false;

  return (
    <div className="flex flex-col items-center justify-center text-center">
      <div className="mb-8 flex w-full flex-col gap-3 px-8 py-5">
        <h2 className="text-xl font-bold text-text-primary">
          <T _str="Refund" />
        </h2>
        <p>
          <T _str="Choose which items to refund or refund whole order" />
        </p>
        <Button
          className="w-fit"
          onClick={() => {
            setSelectedOrderLines(formattedOrderLines.map((o) => o.id));
          }}
          variant="secondary"
        >
          <T _str="Choose all" />
        </Button>
        <div className="border-b border-divider-main">
          {formattedOrderLines.map((o) => {
            return (
              <div
                className="flex items-center justify-between gap-3 border-t border-divider-main py-4"
                key={o.id}
              >
                {o.refunded ? (
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 font-semibold text-danger-dark">
                      <FontAwesomeIcon icon={faInfoCircle} />
                      <p>
                        <T _str="Refunded" />
                      </p>
                    </div>
                    <p className="text-text-disabled">{o.name}</p>
                  </div>
                ) : (
                  <CheckboxButton
                    checked={selectedOrderLines.includes(o.id)}
                    className="w-full"
                    label={o.name}
                    onClick={(checked) => {
                      setSelectedOrderLines((prev) =>
                        checked
                          ? [...prev, o.id]
                          : prev.filter((id) => id !== o.id)
                      );
                    }}
                  />
                )}

                <p>{o.formattedPrice}</p>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex w-full flex-col gap-5 bg-background-secondary px-8 py-5">
        <div className="flex items-center justify-between">
          <p>
            <T _str="Payment method" />
          </p>
          <p>{paymentMethods}</p>
        </div>

        <div className="flex gap-3">
          <Button onClick={onClose} variant="danger">
            <T _str="Cancel" />
          </Button>

          <Button
            className="flex-1"
            onClick={() => {
              refundActions.setOrderLines({
                order,
                selectedOrderLines,
                formattedOrderLines
              });

              onConfirm();
            }}
          >
            {isBilling ? (
              <T _str="Confirm" />
            ) : (
              <T _str="Choose refund method" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

type ConfirmDialogProps = {
  title: string;
  onConfirm: () => void;
  onOutsideClick?: () => void;
  children?: React.ReactNode;
};

const ConfirmDialog = ({
  title,
  onConfirm,
  children,
  ...props
}: ConfirmDialogProps) => {
  return (
    <div className="flex flex-col items-center p-8">
      <div className="mb-6 flex size-8 items-center justify-center rounded-full bg-primary-main text-primary-contrast">
        <FontAwesomeIcon className="h-1/2" icon={faCheck} />
      </div>

      {title ? (
        <p className="text-center text-xl font-semibold">
          <T _str={title} />
        </p>
      ) : null}

      {children}

      <div className="mt-6 flex justify-end gap-3">
        {props.onOutsideClick ? (
          <Button onClick={props.onOutsideClick} variant="danger">
            <T _str="Cancel" />
          </Button>
        ) : null}

        <Button onClick={onConfirm} variant="primary">
          <T _str="Confirm" />
        </Button>
      </div>
    </div>
  );
};

type ChooseRefundMethodDialogProps = {
  isFullSwish?: boolean;
  order: OrderWithLines;
  onConfirm: () => void;
  onOutsideClick: () => void;
  currency: Currency;
  provider: PaymentProvider | undefined;
};

const ChooseRefundMethodDialog = ({
  isFullSwish,
  order,
  onConfirm,
  onOutsideClick,
  currency,
  provider
}: ChooseRefundMethodDialogProps) => {
  const t = useT();

  const {
    hardware: { printer }
  } = useAppContext();

  const { locale } = useCurrentTillLocalisation();

  const refundInfo = useRefundInfo();

  // Find discounts that only apply to the selected order lines
  const hybridBillingDiscountTotal = useMemo(() => {
    const hybridDiscounts = order.discounts?.filter(
      (discount) => discount.discountType === "discount-hybridBilling"
    );

    return refundInfo.selectedOrderLines.reduce((acc, curr) => {
      const discount = hybridDiscounts?.find(
        (d) =>
          d.items?.find(
            (i) =>
              i.orderLineId === curr.orderLineId ||
              i.productLineId === curr.productLineId
          )
      );

      if (!discount) return acc;

      return acc + discount.unitDiscountedAmount;
    }, 0);
  }, [order.discounts, refundInfo.selectedOrderLines]);

  const unitTotalRefundAmountInclVat = refundInfo.selectedOrderLines.reduce(
    (acc, { amount, unitRefundAmountInclVat }) =>
      acc + amount * unitRefundAmountInclVat,
    0
  );

  return (
    <>
      <div className="flex flex-col items-center justify-center text-center">
        <div className="mb-8 flex w-full flex-col gap-3 px-8 py-5">
          <h2 className="text-xl font-bold text-text-primary">
            <T _str="Refund" />
          </h2>
          <p>
            <T _str="Choose refund method" />
          </p>

          <div className="flex flex-col">
            {provider === "adyen" && isFullSwish ? (
              <div className="flex items-center justify-between border-y border-divider-main py-4">
                <RadioButton
                  checked={refundInfo.refundMethod === "swish"}
                  label="Swish"
                  onClick={() => {
                    refundActions.setRefundMethod("swish");
                  }}
                />
                <p>
                  {priceFormatter({
                    value: unitTotalRefundAmountInclVat,
                    currency,
                    locale
                  })}
                </p>
              </div>
            ) : null}

            {provider && !isFullSwish ? (
              <div className="flex items-center justify-between border-y border-divider-main py-4">
                <RadioButton
                  checked={refundInfo.refundMethod === "card"}
                  label={t("Card")}
                  onClick={() => {
                    refundActions.setRefundMethod("card");
                  }}
                />
                <p>
                  {priceFormatter({
                    value: unitTotalRefundAmountInclVat,
                    currency,
                    locale
                  })}
                </p>
              </div>
            ) : null}

            {printer?.hasCashDrawer ? (
              <div className="flex items-center justify-between border-b border-divider-main py-4">
                <RadioButton
                  checked={refundInfo.refundMethod === "cash"}
                  label={t("Cash")}
                  onClick={() => {
                    refundActions.setRefundMethod("cash");
                  }}
                />
                <p>
                  {priceFormatter({
                    value: unitTotalRefundAmountInclVat,
                    currency,
                    locale
                  })}
                </p>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="flex w-full flex-col gap-5 bg-background-secondary px-8 py-5">
        <div className="flex items-center justify-between">
          <p>
            <T _str="To be refunded" />
          </p>
          {priceFormatter({
            value: unitTotalRefundAmountInclVat,
            currency,
            locale
          })}
        </div>

        {hybridBillingDiscountTotal > 0 && (
          <p className="text-sm text-text-secondary">
            <T _str="* If allowance was used, the user's allowance will be restored" />
          </p>
        )}

        <div className="flex gap-3">
          <Button onClick={onOutsideClick} variant="danger">
            <T _str="Cancel" />
          </Button>

          <Button className="flex-1" onClick={onConfirm}>
            <T _str="Confirm" />
          </Button>
        </div>
      </div>
    </>
  );
};

export const ErrorMessage = ({ error }: { error?: React.ReactNode }) => (
  <div className="flex flex-col items-center gap-y-5 text-center">
    <FontAwesomeIcon className="text-5xl text-danger-dark" icon={faWarning} />

    <p className="text-xl">
      {typeof error === "string" ? (
        <T _str={error || "Something went wrong"} />
      ) : (
        error
      )}
    </p>
  </div>
);
